import React from "react";
import { toast } from "react-toastify";
import { GoogleLogin } from "@react-oauth/google";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { GoogleIcon } from "../../assets/icons";
import { AppleIcon } from "../../assets/icons";
import { FacebookIcon } from "../../assets/icons";
import DataUpLogo from "../../assets/images/DataUpLogoAuth.svg";
import { Button } from "../Button";
import { Input, SelectInput } from "../Input";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import { signup } from "../../features/store/authSlice";
import countries from "../../data/countries.json";
import languages from "../../data/languages.json";
import { loginGoogle } from "../../features/store/authSlice";
import {
  SignUpFormContainerProps,
  SignUpFormProps,
  PasswordStrengthIndicatorProps,
  SubmitButtonProps,
  SignUpFormData,
} from "./types";
import useClickOutside from "../../hooks/useClickOutside";
import { useSocialLogin } from "../../context/SocialLoginConnect";

export const SignUpFormContainer: React.FC<SignUpFormContainerProps> = ({
  onSignIn,
  onSubmit,
  error,
  closeSignUpModal, // <-- Ensure this is included
}) => {
  const clickRef = useClickOutside(closeSignUpModal);

  const dispatch = useAppDispatch();
  const { googleLoginEnabled } = useSocialLogin();

  const handleGoogleLogin = async (credentialResponse: any) => {
    if (!googleLoginEnabled) {
      toast.error(
        "Google login is currently disabled. Please enable it in your account settings.",
      );
      return;
    }

    if (!credentialResponse?.credential) {
      toast.error("Google login failed - no credentials received");
      return;
    }
    try {
      await dispatch(loginGoogle(credentialResponse.credential)).unwrap();
      toast.success("🎉 Logged in successfully!");
      closeSignUpModal();
    } catch (error: any) {
      toast.error(error.message || "Google login failed. Please try again.");
    }
  };
  const SocialLoginButtons = () => (
    <div className="flex gap-4 justify-center">
      {googleLoginEnabled && (
        <GoogleLogin
          onSuccess={handleGoogleLogin}
          onError={() => toast.error("Google login failed.")}
        />
      )}
    </div>
  );

  return (
    <div ref={clickRef}>
      <Logo />
      <Title />
      <div className="space-y-4">
        <SocialLoginButtons />
        <OrDivider />
        <SignUpForm onSubmit={onSubmit} closeSignUpModal={closeSignUpModal} />
        {error && <p className="text-red-500 text-sm">{error}</p>}
        <FooterLinks onSignIn={onSignIn} />
      </div>
    </div>
  );
};

const Logo = () => (
  <div className="flex justify-center mb-4">
    <img
      src={DataUpLogo}
      alt="DatauplogoAuth"
      className="w-[135.55px] h-[32px]"
    />
  </div>
);

const Title = () => (
  <h2 className="text-xl font-bold text-center mb-6 dark:text-white">
    Create an account
  </h2>
);

const SocialButton = ({ icon }: { icon: React.ReactNode }) => (
  <button className="p-2 border rounded-md w-32 flex items-center justify-center">
    {icon}
  </button>
);

const OrDivider = () => (
  <div className="flex items-center my-4">
    <div className="flex-1 border-t border-gray-300"></div>
    <div className="px-4 text-gray-500 text-sm dark:text-white">OR</div>
    <div className="flex-1 border-t border-gray-300"></div>
  </div>
);

const SignUpForm = ({ closeSignUpModal }: SignUpFormProps) => {
  const schema = yup
    .object({
      email: yup.string().required().email().label("Email"),
      username: yup.string().required().label("Username"),
      firstname: yup.string().required().label("Firstname"),
      lastname: yup.string().required().label("Lastname"),
      othername: yup.string().optional().label("Othername"),
      phone_number: yup.string().required().label("Phone Number"),
      country: yup.string().required().label("Country"),
      language: yup.string().required().label("Language"),
      password: yup.string().required().label("Password"),
    })
    .required();

  const { register, handleSubmit, reset, watch } = useForm<SignUpFormData>({
    resolver: yupResolver(schema),
  });

  const dispatch = useAppDispatch();

  const { loading } = useAppSelector((state) => state.auth);

  const onFormSubmit = async (data: SignUpFormData) => {
    await dispatch(signup(data))
      .unwrap()
      .then(() => {
        toast.success("🎉 Account created successfully!");
        reset();
        closeSignUpModal();
      })
      .catch((error) => {
        toast.error(error);
      });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          type="text"
          placeholder="First Name"
          register={register}
          name="firstname"
          required
          // error={errors.firstname?.message}
        />

        <Input
          type="text"
          placeholder="Last Name"
          register={register}
          name="lastname"
          required
          // error={errors.lastname?.message}
        />
      </div>

      <Input
        type="text"
        placeholder="Other Name"
        register={register}
        name="othername"
        // error={errors.othername?.message}
      />

      <Input
        type="text"
        placeholder="Username"
        register={register}
        name="username"
        required
        // error={errors.username?.message}
      />

      <Input
        type="email"
        placeholder="Email address"
        register={register}
        name="email"
        required
        // error={errors.email?.message}
      />

      <Input
        type="text"
        placeholder="Phone Number"
        register={register}
        name="phone_number"
        required
        // error={errors.phone_number?.message}
      />

      <SelectInput
        options={countries}
        register={register}
        name="country"
        placeholder="Select Country"
        required
        // error={errors.country?.message}
      />

      <SelectInput
        options={languages}
        register={register}
        name="language"
        placeholder="Language"
        required
        // error={errors.language?.message}
      />

      <Input
        type="password"
        placeholder="Password"
        isPassword
        register={register}
        name="password"
        required
        // error={errors.password?.message}
      />
      <PasswordStrengthIndicator password={watch("password")} />
      <TermsAndConditions />
      <SubmitButton loading={loading} />
    </form>
  );
};

const PasswordStrengthIndicator = ({
  password,
}: PasswordStrengthIndicatorProps) => {
  const getStrength = (pw: string) => {
    if (!pw) return 0;
    if (pw.length < 4) return 1;
    if (pw.length < 8) return 2;
    if (!/[A-Z]/.test(pw) || !/[0-9]/.test(pw)) return 3;
    return 4;
  };

  const strength = getStrength(password);
  const strengthText = ["", "Very Weak", "Weak", "Medium", "Strong"];

  return (
    <div className="space-y-1">
      <p className="text-xs text-gray-500 dark:text-white">
        {password
          ? `Strength: ${strengthText[strength]}`
          : "Must be at least 8 characters"}
      </p>
      <div className="w-full h-1 flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`flex-1 ${
              i <= strength
                ? strength < 3
                  ? "bg-red-500"
                  : strength < 4
                    ? "bg-yellow-500"
                    : "bg-green-500"
                : "bg-gray-200"
            } ${i === 1 ? "rounded-l-full" : ""} ${
              i === 5 ? "rounded-r-full" : ""
            } ml-0.5`}
          />
        ))}
      </div>
    </div>
  );
};

const TermsAndConditions = () => {
  return (
    <div className="relative">
      <p className="text-xs text-gray-500 dark:text-white">
        By creating an account, you agree to the{" "}
        <span className=" text-[#039EF2]">Terms & Conditions</span>.
      </p>
    </div>
  );
};

const SubmitButton = ({ loading }: SubmitButtonProps) => (
  <Button
    type="submit"
    bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
    textColor="text-white"
    className="w-full h-10 flex items-center justify-center"
    borderRadius="rounded-lg"
    disabled={loading}
  >
    {loading ? (
      <div className="flex items-center">
        <svg
          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        Processing...
      </div>
    ) : (
      "Complete Sign Up"
    )}
  </Button>
);

const FooterLinks = ({ onSignIn }: { onSignIn: () => void }) => (
  <p className="text-center dark:text-white text-xs">
    Already have an account?{" "}
    <span
      className="text-[#039EF2] font-bold cursor-pointer"
      onClick={onSignIn}
    >
      Sign In
    </span>
  </p>
);
